import { useState } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { Button } from "components";

// Styled Components
const ContactContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
`;

const ContactTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const ContactForm = styled(motion.form)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FormGroup = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${(props) => props.theme.spacing.sm};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${(props) => props.theme.borderRadius.sm};
  background: rgba(255, 255, 255, 0.1);
  color: ${(props) => props.theme.colors.text};
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${(props) => props.theme.spacing.sm};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${(props) => props.theme.borderRadius.sm};
  background: rgba(255, 255, 255, 0.1);
  color: ${(props) => props.theme.colors.text};
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const ContactInfo = styled(motion.div)`
  margin-top: ${(props) => props.theme.spacing.xl};
  text-align: center;
`;

const InfoTitle = styled.h2`
  color: ${(props) => props.theme.colors.text};
  font-size: 1.5rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const InfoItem = styled.div`
  color: ${(props) => props.theme.colors.textLight};
  margin-bottom: ${(props) => props.theme.spacing.sm};
  font-size: 1.1rem;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(74, 222, 128, 0.2);
  border: 1px solid rgba(74, 222, 128, 0.4);
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.md};
  color: #4ade80;
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

// Animation variants for form only

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const successVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log("Form submitted:", formData);
    setIsSubmitted(true);

    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", subject: "", message: "" });
    }, 3000);
  };

  return (
    <ContactContainer>
      <ContactTitle>Contact Us</ContactTitle>

      <ContactForm
        onSubmit={handleSubmit}
        variants={formVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.1 }}
      >
        {isSubmitted && (
          <SuccessMessage
            variants={successVariants}
            initial="hidden"
            animate="visible"
          >
            Thank you for your message! We'll get back to you soon.
          </SuccessMessage>
        )}

        <FormGroup>
          <Label htmlFor="name">Name</Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="subject">Subject</Label>
          <Input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="What's this about?"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="message">Message</Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us more..."
            required
          />
        </FormGroup>

        <Button type="submit" variant="primary" fullWidth>
          Send Message
        </Button>
      </ContactForm>

      <ContactInfo
        variants={formVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.2 }}
      >
        <InfoTitle>Get in Touch</InfoTitle>
        <InfoItem>📧 Email: contact@telazercom.com</InfoItem>
        <InfoItem>📱 Phone: +1 (555) 123-4567</InfoItem>
        <InfoItem>📍 Address: 123 Tech Street, Digital City, DC 12345</InfoItem>
        <InfoItem>⏰ Hours: Monday - Friday, 9:00 AM - 6:00 PM</InfoItem>
      </ContactInfo>
    </ContactContainer>
  );
}

export default Contact;
